"use client";
import { Logger } from "@shared/utils/logger";
import { useEffect, useRef, useCallback, useState } from "react";

/**
 * Return type for the useDocumentPictureInPicture hook.
 */
export interface UseDocumentPipReturn {
  isPipActive: boolean; /** Whether PiP is currently active */
  isPipSupported: boolean; /** Whether the browser supports any PiP API */
  isDocumentPipSupported: boolean; /** Whether Document PiP is supported (Chrome/Edge) */
  isVideoPipSupported: boolean; /** Whether Video element PiP is supported (Firefox/Safari fallback) */
  pipWindow: Window | null; /** The PiP window instance, or null if closed */
  openPipWindow: () => Promise<void>; /** Manually open the PiP window (requires user gesture) */
  closePipWindow: () => void; /** Manually close the PiP window */
}

interface UseDocumentPipOptions {
  /** Whether the feature is enabled (e.g. not leaving meeting) */
  isActive?: boolean;
  /** Callback when PiP closes */
  onClose?: () => void;
}

// Extend Window interface for Document PiP API
declare global {
  interface Window {
    documentPictureInPicture?: {
      requestWindow: (options?: { width?: number; height?: number }) => Promise<Window>;
    };
  }
}

/**
 * Check if Document PiP API is supported.
 */
const isDocumentPipSupported = (): boolean => {
  return typeof window !== "undefined" && "documentPictureInPicture" in window;
};

/**
 * Check if Video Element PiP API is supported (fallback for Firefox/Safari).
 */
const isVideoPipSupported = (): boolean => {
  if (typeof document === "undefined") return false;
  return "pictureInPictureEnabled" in document && document.pictureInPictureEnabled === true;
};

/**
 * usePictureInPicture - Manages the lifecycle of a Document PiP window.
 * Returns the window instance so React Portals can be rendered into it.
 */
export const usePictureInPicture = ({ isActive = true, onClose }: UseDocumentPipOptions = {}): UseDocumentPipReturn => {
  const [isPipActive, setIsPipActive] = useState(false);
  const [pipWindow, setPipWindow] = useState<Window | null>(null);

  const docPipSupported = isDocumentPipSupported();
  const vidPipSupported = isVideoPipSupported();
  const isPipSupported = docPipSupported || vidPipSupported;

  const pipWindowRef = useRef<Window | null>(null);
  const isOpeningRef = useRef(false); // Prevent concurrent opening attempts

  // Track manual close to prevent auto-reopening immediately
  const wasManuallyClosedRef = useRef(false);

  // Track last user interaction time to handle activation state
  const lastInteractionTimeRef = useRef<number>(0);
  const hasUserGestureRef = useRef(false);

  // Timer for auto-closing PiP if opened manually while on the same tab
  const autoCloseTimerRef = useRef<NodeJS.Timeout | null>(null);
  const pipOpenTimeRef = useRef<number>(0);

  /**
   * Close the PiP window.
   */
  const closePipWindow = useCallback(() => {
    if (autoCloseTimerRef.current) {
      clearTimeout(autoCloseTimerRef.current);
      autoCloseTimerRef.current = null;
    }

    if (pipWindowRef.current) {
      pipWindowRef.current.close();
      pipWindowRef.current = null;
      setPipWindow(null);
      setIsPipActive(false);
      wasManuallyClosedRef.current = false; // Reset so it can auto-open again next time
      onClose?.();
    }
  }, [onClose]);

  /**
   * Open the Document PiP window.
   * Note: This only works for Document PiP (Chrome/Edge).
   * For Firefox/Safari, use useVideoPictureInPicture hook instead.
   */
  const openPipWindow = useCallback(async () => {
    // Prevent concurrent opening or reopening an already open window
    if (!docPipSupported || !isActive || pipWindowRef.current || isOpeningRef.current) return;

    isOpeningRef.current = true;

    try {
      // Check if browser supports documentPictureInPicture
      if (!window.documentPictureInPicture) {
        Logger.warn("PiP", "Document Picture-in-Picture API is not supported in this browser");
        isOpeningRef.current = false;
        return;
      }

      // dimensions typical for video calls (optimized for 16:9 aspect ratio)
      const win = await window.documentPictureInPicture.requestWindow({
        width: 400,
        height: 300
      });

      pipWindowRef.current = win;
      setPipWindow(win);
      setIsPipActive(true);
      wasManuallyClosedRef.current = false;
      pipOpenTimeRef.current = Date.now();
      isOpeningRef.current = false;

      // Handle window close (user clicked X)
      win.addEventListener("pagehide", () => {
        pipWindowRef.current = null;
        setPipWindow(null);
        setIsPipActive(false);
        if (document.hidden) {
          wasManuallyClosedRef.current = true;
        }
        onClose?.();
      });
    } catch {
      isOpeningRef.current = false;
      // Silently handle errors - expected when user hasn't interacted recently
    }
  }, [docPipSupported, isActive, onClose]);

  /**
   * Monitor isActive state and close PiP if it becomes false
   */
  useEffect(() => {
    if (!isActive && isPipActive) {
      closePipWindow();
    }
  }, [isActive, isPipActive, closePipWindow]);

  /**
   * If PiP was disabled while screen sharing and sharing ends while the Meet
   * tab is still hidden, no new visibilitychange event is fired. Try to
   * restore PiP when the feature becomes active again in that state.
   */
  useEffect(() => {
    if (!docPipSupported || !isActive || !document.hidden || pipWindowRef.current || wasManuallyClosedRef.current) {
      return;
    }

    const reopenTimer = window.setTimeout(() => {
      void openPipWindow();
    }, 0);

    return () => window.clearTimeout(reopenTimer);
  }, [docPipSupported, isActive, openPipWindow]);

  /**
   * Track user interactions to enable PiP
   */
  useEffect(() => {
    if (!docPipSupported || !isActive) return;

    // Listen for user interactions that activate transient activation
    const interactionEvents = ["click", "touchstart", "keydown", "mousedown"];

    const handleUserInteraction = () => {
      lastInteractionTimeRef.current = Date.now();
      hasUserGestureRef.current = true;
    };

    interactionEvents.forEach((event) => {
      document.addEventListener(event, handleUserInteraction, { passive: true });
    });

    return () => {
      interactionEvents.forEach((event) => {
        document.removeEventListener(event, handleUserInteraction);
      });
    };
  }, [docPipSupported, isActive]);

  /**
   * Handle tab visibility change (Auto PiP).
   * Only for Document PiP - Firefox/Safari use useVideoPictureInPicture.
   */
  useEffect(() => {
    if (!docPipSupported || !isActive) return;

    // Disable auto-PiP on mobile devices (small screens)
    const isMobileScreen = typeof window !== "undefined" && window.innerWidth < 768;
    if (isMobileScreen) {
      return;
    }

    const handleVisibilityChange = () => {
      if (document.hidden) {
        if (autoCloseTimerRef.current) {
          clearTimeout(autoCloseTimerRef.current);
          autoCloseTimerRef.current = null;
        }

        if (!wasManuallyClosedRef.current) {
          openPipWindow();
        }
      } else {
        closePipWindow();
      }
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);

    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, [docPipSupported, isActive, openPipWindow, closePipWindow]);

  /**
   * Cleanup on unmount
   */
  useEffect(() => {
    return () => {
      closePipWindow();
    };
  }, [closePipWindow]);

  return {
    isPipActive,
    isPipSupported,
    isDocumentPipSupported: docPipSupported,
    isVideoPipSupported: vidPipSupported,
    pipWindow,
    openPipWindow,
    closePipWindow
  };
};

/**
 * Helper to focus the main meeting tab from PiP window
 */
export const focusMeetingTab = (): void => {
  if (typeof window !== "undefined") {
    window.focus();
  }
};
